import { RouteRecordRaw } from 'vue-router'
import { ISessionStore } from '@/store/pinia/SessionStore.ts'
import { IUserStore } from '@/store/pinia/UserStore.ts'
import { IAccountFlagsStore } from '@/store/pinia/AccountFlagsStore.ts'
import { adviceRouteGuard } from '@/composables/adviceRouteGuard.ts'
import { otivoAdminGuard } from '@/router/clients/cfs/cfsRouteGuards.ts'
import {
  ExclusionRules,
  useWhitelabelExclusionRules,
} from '@/composables/useWhitelabelExclusionRules.ts'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'

export default (
  sessionStore: ISessionStore,
  userStore: IUserStore,
  accountFlagsStore: IAccountFlagsStore
): Array<RouteRecordRaw> => {
  const { hasExclusionRule } = useWhitelabelExclusionRules()
  const enableDebtRoutes = !hasExclusionRule(ExclusionRules.DEBT_MODULE)
  const enableRetirementRoutes =
    !hasExclusionRule(ExclusionRules.RETIREMENT_PLAN) &&
    useLDFlag(FeatureList.ENABLE_RETIREMENT_PLAN, false).value

  const baseRoutes: Array<RouteRecordRaw> = [
    {
      path: '/admin',
      name: 'admin.login',
      component: () => import('@/views/LoadingPage.vue'),
      beforeEnter: () => {
        sessionStore.login('Username-Password-Authentication')
      },
    },
    {
      path: '/admin-portal',
      redirect: '/admin-portal/home',
      component: () => import('@/app/layouts/AdminPortalLayout.vue'),
      beforeEnter: (to, from, next) => {
        otivoAdminGuard(to, from, next, userStore, sessionStore)
      },
      meta: {
        isAdminPortal: true,
      },
      children: [
        {
          path: 'home',
          name: 'admin.home',
          component: () => import('@/features/admin-portal/views/AdminUserView.vue'),
        },
        {
          path: 'search-users',
          name: 'admin.searchForm',
          component: () => import('@/components/AdminPortal/SearchForm.vue'),
        },
        {
          path: 'reporting',
          name: 'admin.portalReporting',
          component: () => import('@/features/admin-portal/views/ReportingView.vue'),
          redirect: { name: 'admin.portalReportingOverview' },
          children: [
            {
              path: 'overview',
              name: 'admin.portalReportingOverview',
              component: () => import('@/components/CallcentrePortal/CallcentrePortalOverview.vue'),
              meta: {
                title: 'Reporting overview',
              },
            },
            {
              path: 'engagement',
              name: 'admin.portalReportingEngagement',
              component: () =>
                import('@/components/CallcentrePortal/CallcentrePortalEngagement.vue'),
              meta: {
                title: 'Member engagement',
              },
            },
            {
              path: 'wealth-health-check',
              name: 'admin.portalReportingWealthHealthCheck',
              component: () =>
                import('@/components/CallcentrePortal/CallcentrePortalWealthHealthReport.vue'),
              meta: {
                title: 'Wealth health check',
              },
            },
          ],
        },
        // Manage User Routes
        {
          path: 'manage-user',
          name: 'admin.manageUser',
          component: () => import('@/components/AdminPortal/UserInfoForm.vue'),
        },
        {
          path: 'account-status',
          name: 'admin.accountStatus',
          components: {
            default: () => import('@/components/AdminPortal/AccountStatus.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
        },
        {
          path: 'details',
          name: 'admin.clientDetails',
          components: {
            default: () => import('@/components/CFS/Guidance/UserProfile.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
        },
        {
          path: 'financial-summary',
          name: 'admin.clientFinancialSummary',
          components: {
            default: () => import('@/components/FinancialSummary/FinancialSummary.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
        },
        // End Managed User Routes
        // Start Advice Routes
        {
          path: 'super-investments',
          name: 'admin.superInvestments',
          components: {
            default: () => import('@/components/CFS/IntrafundInvestment/SuperInvestments.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
          beforeEnter: (to, from, next) =>
            adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
        },
        {
          path: 'salary-sacrifice-contributions',
          name: 'admin.salarySacrificeContributions',
          components: {
            default: () =>
              import('@/components/CFS/SalarySacrifice/SalarySacrificeContribution.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
          beforeEnter: (to, from, next) =>
            adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
        },
        {
          path: 'lump-sum-concessional-contributions',
          name: 'admin.lumpSumConcessionalContributions',
          components: {
            default: () => import('@/components/CFS/LumpSumContributions.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
          props: {
            default: { type: 'concessional' },
          },
          beforeEnter: (to, from, next) =>
            adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
        },
        {
          path: 'lump-sum-non-concessional-contributions',
          name: 'admin.lumpSumNonConcessionalContributions',
          components: {
            default: () => import('@/components/CFS/LumpSumContributions.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
          props: {
            default: { type: 'non_concessional' },
          },
          beforeEnter: (to, from, next) =>
            adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
        },
        {
          path: 'personal-insurance',
          name: 'admin.personalInsurance',
          components: {
            default: () => import('@/views/Otivo/Dashboard/Insurance/InsuranceView.vue'),
            sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
          },
          beforeEnter: (to, from, next) =>
            adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
        },
        // End Advice Routes
      ],
    },
  ]

  const debtRoutes: Array<RouteRecordRaw> = [
    {
      path: 'debt-plan',
      name: 'admin.debtPlan',
      components: {
        default: () => import('@/views/Otivo/Dashboard/Debt/DebtPlan.vue'),
        sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
      },
      beforeEnter: (to, from, next) =>
        adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
    },
    {
      path: 'debt',
      name: 'admin.DebtManagement',
      components: {
        default: () => import('@/views/Otivo/Dashboard/Debt/DebtManagementView.vue'),
        sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
      },
      beforeEnter: (to, from, next) =>
        adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
      children: [
        {
          path: ':type/:id',
          name: 'admin.DebtDetails',
          component: () => import('@/views/Otivo/Dashboard/Debt/Pages/DebtDetails.vue'),
        },
        {
          path: 'overview/:type/:id',
          name: 'admin.DebtDetailsOverview',
          component: () => import('@/views/Otivo/Dashboard/Debt/Pages/DebtDetailsOverview.vue'),
        },
      ],
    },
  ]

  const retirementRoutes: Array<RouteRecordRaw> = [
    {
      path: 'retirement-plan',
      name: 'admin.retirementPlan',
      components: {
        default: () => import('@/app/components/module-upsells/AdviceModule.vue'),
        sidebar: () => import('@/features/admin-portal/components/UserDetailsSidenav.vue'),
      },
      props: {
        default: { module: 'retirementPlan' },
      },
      beforeEnter: (to, from, next) =>
        adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
    },
  ]

  const routes: Array<RouteRecordRaw> = [...baseRoutes]

  if (enableDebtRoutes) {
    const adminPortalRoute = routes.find((route) => route.path === '/admin-portal')
    if (adminPortalRoute?.children) {
      adminPortalRoute.children.push(...debtRoutes)
    }
  }

  if (enableRetirementRoutes) {
    const adminPortalRoute = routes.find((route) => route.path === '/admin-portal')
    if (adminPortalRoute?.children) {
      adminPortalRoute.children.push(...retirementRoutes)
    }
  }

  return routes
}
